// src/pages/Login.js
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { loginUser } from "../utils/api";
import { getLogoUrl } from "../utils/logoUtils";
import "./Login.css";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCustomerCareLogin, setIsCustomerCareLogin] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [logoUrl, setLogoUrl] = useState(getLogoUrl());

  // Initialize theme and log component mount
  React.useEffect(() => {
    // #region agent log
    const logDataLoginMount = {
      location: 'Login.js:useEffect-mount',
      message: 'Login component mounted',
      data: {
        pathname: window.location.pathname,
        href: window.location.href,
        hostname: window.location.hostname,
        isProduction: process.env.NODE_ENV === 'production',
        referrer: document.referrer
      },
      timestamp: Date.now(),
      sessionId: 'debug-session',
      runId: 'refresh-debug',
      hypothesisId: 'R1'
    };
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(logDataLoginMount)
    }).catch(() => {});
    // #endregion
    
    const savedTheme = localStorage.getItem('theme') || 'light';
    const darkMode = savedTheme === 'dark';
    setIsDarkMode(darkMode);
    document.documentElement.setAttribute('data-theme', savedTheme);
    setLogoUrl(getLogoUrl());
  }, []);

  // Update logo when theme changes
  React.useEffect(() => {
    setLogoUrl(getLogoUrl());
  }, [isDarkMode]);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    const theme = newDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
    setLogoUrl(getLogoUrl());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (isCustomerCareLogin) {
      if (!userId || !password) {
        setMessage("Please fill in both User ID and Password");
        return;
      }
    } else {
      if (!email || !password) {
        setMessage("Please fill in both Email and Password");
        return;
      }
    }
    
    setIsLoading(true);
    setMessage('');
    
    try {
      const loginData = isCustomerCareLogin 
        ? { userId, password }
        : { email, password };
        
      const response = await loginUser(loginData);
      
      if (response.success) {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Login.js:handleSubmit-success',message:'Login successful',data:{hasToken:!!response.token,hasUser:!!response.user,userRole:response.user?.role,tokenStored:!!localStorage.getItem('token'),tokenLength:localStorage.getItem('token')?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'session-debug',hypothesisId:'L1'})}).catch(()=>{});
        // #endregion agent log
        
        setMessage("Login successful!");
        
        // Get the user role and redirect to appropriate dashboard
        const userRole = response.user.role;
        const userRoleLower = userRole.toLowerCase();
        
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Login.js:handleSubmit-redirect',message:'Redirecting to dashboard',data:{userRole,userRoleLower,redirectPath:userRoleLower==='driver'?'/emergency/driver':userRoleLower==='customercare'?'/dashboard/customer-care':`/dashboard/${userRoleLower}`},timestamp:Date.now(),sessionId:'debug-session',runId:'session-debug',hypothesisId:'L2'})}).catch(()=>{});
        // #endregion agent log
        
        // Handle different role redirects
        if (userRoleLower === 'driver') {
          navigate('/emergency/driver');
        } else if (userRoleLower === 'customercare') {
          navigate('/dashboard/customer-care');
        } else {
          navigate(`/dashboard/${userRoleLower}`);
        }
      } else {
        setMessage(response.message || "Login failed");
      }
    } catch (error) {
      console.error('Login error:', error);
      // Display the specific error message from the API
      const errorMessage = error.message || "Error logging in. Please try again later.";
      setMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="h-screen w-full flex items-center justify-center p-4 relative overflow-y-auto login-page" 
      style={{ 
        fontFamily: "'Inter', sans-serif",
        backgroundImage: `url(https://res.cloudinary.com/dmmetrvda/image/upload/v1734707300/weheal-backgrounds/login-background.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Back to Home Button */}
      <Link
        to="/"
        className="fixed top-4 left-4 z-50 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-200 flex items-center gap-2 group"
        title="Back to Home"
        aria-label="Back to Home"
      >
        <svg className="w-5 h-5 text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors hidden sm:inline">Home</span>
      </Link>

      {/* Dark Mode Toggle Button */}
      <button
        onClick={toggleDarkMode}
        className="fixed top-4 right-4 z-50 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-200 dark-mode-toggle-btn"
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {isDarkMode ? (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/20 to-transparent login-overlay"></div>
      
      {/* Content Container */}
      <div className="w-full max-w-md relative z-10 my-auto">
        {/* Logo & Header */}
        <div className="text-center mb-5">
          <Link to="/" className="inline-block mb-3">
            <div className="inline-flex items-center gap-2">
              <img 
                src={logoUrl} 
                alt="WeHeal" 
                className={isDarkMode ? "h-10 w-auto" : "h-14 w-auto"}
                onError={(e) => {
                  // Fallback to text logo if image fails
                  e.target.style.display = 'none';
                  const parent = e.target.parentElement;
                  if (parent && !parent.querySelector('.fallback-logo')) {
                    const fallback = document.createElement('div');
                    fallback.className = `fallback-logo ${isDarkMode ? 'w-10 h-10' : 'w-14 h-14'} bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center shadow-lg`;
                    fallback.innerHTML = '<span class="text-white text-xl font-bold">W</span>';
                    parent.insertBefore(fallback, e.target);
                  }
                }}
              />
            </div>
          </Link>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
            Welcome Back
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-xs">
            Sign in to continue to your healthcare dashboard
          </p>
        </div>

        {/* Modern Glass Card */}
        <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-2xl shadow-2xl border border-white/20 dark:border-gray-700/50 p-6 login-card">
          {/* Customer Care Toggle */}
          {!isCustomerCareLogin && (
            <div className="mb-4 text-center">
              <button
                type="button"
                onClick={() => setIsCustomerCareLogin(true)}
                className="text-xs text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors font-medium"
              >
                Staff Login? Click here
              </button>
            </div>
          )}

          {isCustomerCareLogin && (
            <div className="mb-4 bg-blue-50/80 dark:bg-blue-900/30 backdrop-blur-sm border border-blue-200/50 dark:border-blue-700/50 rounded-xl p-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">Customer Care Officer Login</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mb-2">Use your assigned User ID to login</p>
                  <button
                    type="button"
                    onClick={() => setIsCustomerCareLogin(false)}
                    className="text-xs text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-200 font-medium underline"
                  >
                    Switch to regular login
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email/User ID Field */}
            <div>
              <label 
                htmlFor={isCustomerCareLogin ? "userId" : "email"} 
                className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5"
              >
                {isCustomerCareLogin ? "User ID" : "Email Address"}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {isCustomerCareLogin ? (
                    <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>
                <input
                  id={isCustomerCareLogin ? "userId" : "email"}
                  name={isCustomerCareLogin ? "userId" : "email"}
                  type={isCustomerCareLogin ? "text" : "email"}
                  autoComplete={isCustomerCareLogin ? "username" : "email"}
                  required
                  value={isCustomerCareLogin ? userId : email}
                  onChange={(e) => isCustomerCareLogin ? setUserId(e.target.value) : setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border-2 border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm login-input"
                  placeholder={isCustomerCareLogin ? "Enter your User ID" : "you@example.com"}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label 
                htmlFor="password" 
                className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border-2 border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm login-input"
                  placeholder="Enter your password"
                />
                  <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  {showPassword ? (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            {!isCustomerCareLogin && (
              <div className="flex items-center justify-between pt-1">
                <label className="flex items-center cursor-pointer group">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <span className="ml-1.5 text-xs text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-gray-100">Remember me</span>
                </label>
                <Link 
                  to="/forgot-password" 
                  className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 px-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 text-sm"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign in</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="text-center pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-600 dark:text-gray-300">
              Don't have an account?{' '}
              <Link to="/signup" className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
                Create account
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to home
          </Link>
        </div>
      </div>

      {/* Toast Message */}
      {message && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in-up">
          <div className={`rounded-xl shadow-2xl px-6 py-4 max-w-sm backdrop-blur-xl border ${
            message.includes("success") || message.includes("successful")
              ? "bg-green-50/90 border-green-200"
              : "bg-red-50/90 border-red-200"
          }`}>
            <div className="flex items-start gap-3">
              {message.includes("success") || message.includes("successful") ? (
                <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <p className={`text-sm font-medium ${
                message.includes("success") || message.includes("successful")
                  ? "text-green-800"
                  : "text-red-800"
              }`}>
                {message}
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Login;
